/**
 * Supabase Sync Hooks - Central Export
 *
 * These hooks sync call data directly between Redux and Supabase, which is the
 * single durable source of truth. They provide:
 *
 * 1. Local-first updates (instant UI response)
 * 2. Background sync to Supabase (persistence)
 * 3. Cross-device sync (Supabase Realtime)
 *
 * ARCHITECTURE:
 * ┌─────────────────────────────────────────────────────────────────┐
 * │                    DATABASE-FIRST ARCHITECTURE                  │
 * ├─────────────────────────────────────────────────────────────────┤
 * │                                                                 │
 * │  Redux (local) ───────────────────────────────▶ Supabase       │
 * │       │                                          │              │
 * │       │         Redis (same-device tabs)         │              │
 * │       └────────────────────────────────────────▶│              │
 * │                                                  │              │
 * │       Supabase Realtime ◀────────────────────────┘              │
 * │              │                                                  │
 * │              └──▶ Other Devices                                 │
 * │                                                                 │
 * └─────────────────────────────────────────────────────────────────┘
 *
 * USAGE:
 * ```tsx
 * import {
 *   useTranscriptSync,
 *   useNotesSync,
 *   useChatSync,
 *   useAiAnalysisSync,
 *   useCallInfoSync,
 *   useSupabaseRealtime,
 * } from '@/hooks/syncHooks';
 *
 * function Conference({ callId }: { callId: string }) {
 *   // Each hook manages its own sync lifecycle
 *   const transcriptSync = useTranscriptSync({ callId });
 *   const notesSync = useNotesSync({ callId });
 *   const chatSync = useChatSync({ callId });
 *   const aiSync = useAiAnalysisSync({ callId });
 *   const callInfoSync = useCallInfoSync({ callId });
 *
 *   // Optional: Cross-device realtime (needs dbCallId)
 *   const realtime = useSupabaseRealtime({
 *     callId,
 *     dbCallId: transcriptSync.dbCallId,
 *     enabled: !!transcriptSync.dbCallId,
 *   });
 *
 *   // Data flows: Redux → Hooks → Supabase (automatic)
 *   // No manual save needed - hooks handle everything
 * }
 * ```
 */

// User preferences (settings) sync
export {
  useUserPreference,
  useUserPreferencesObject,
  migrateLocalStoragePreference,
  clearPreferencesCache,
} from './useUserPreferences';

// Transcript sync (debounced 500ms)
export {
  useTranscriptSync,
  useTranscriptHydration,
} from './useTranscriptSync';

// Notes sync (debounced 1s for typing)
export {
  useNotesSync,
  useNotesHydration,
} from './useNotesSync';

// Chat sync (near-immediate, 100ms)
export {
  useChatSync,
  useChatHydration,
} from './useChatSync';

// AI Analysis sync (tiered: 200ms/2s/5s)
export {
  useAiAnalysisSync,
  useAiAnalysisHydration,
} from './useAiAnalysisSync';

// Call Info sync (debounced 1s)
export {
  useCallInfoSync,
} from './useCallInfoSync';

// Call Ownership sync (debounced 500ms)
export {
  useCallOwnershipSync,
  useCallOwnershipHydration,
} from './useCallOwnershipSync';

// Tool Sharing sync (debounced 500ms)
export {
  useToolSharingSync,
  useToolSharingHydration,
} from './useToolSharingSync';

// Orchestrator Tree sync (debounced 2s)
export {
  useOrchestratorTreeSync,
  useOrchestratorTreeHydration,
} from './useOrchestratorTreeSync';

// Supabase Realtime (cross-device)
export {
  useSupabaseRealtime,
  useRealtimeBroadcast,
} from './useSupabaseRealtime';

// Types for external use
export type { UseUserPreferencesOptions } from './useUserPreferences';
